import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import threatService from './threatsService'

//set initial state
const initialState = {
    threats: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: ''
}

//create new threat
export const createThreat = createAsyncThunk(
    'threats/create', 
    async (threatData, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await threatService.createThreat(threatData, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data &&
            error.response.data.message) || 
            error.message || 
            error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

//get threats for a bowtie
export const getThreats = createAsyncThunk(
    'threats/getAllForBowtie',
    async (bowtieId, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await threatService.getThreats(bowtieId, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//delete threat
export const deleteThreat = createAsyncThunk(
    'threats/delete',
    async (id, thunkAPI) => {
        try{
            const token = thunkAPI.getState().auth.user.token
            return await threatService.deleteThreat(id, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//update threat
export const updateThreat = createAsyncThunk(
    'threats/update',
    async(threatData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await threatService.updateThreat(threatData, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//Threat Slice
export const threatSlice = createSlice({
    name: 'threats',
    initialState,
    reducers: {
        reset:(state) => initialState,
        // multiuser.md Phase 2c: applied from a socket broadcast (another
        // user's create/update), not a thunk — merges if already present,
        // adds it if this tab doesn't have it yet.
        remoteThreatUpserted: (state, action) => {
            const incoming = action.payload
            const existing = state.threats.find((t) => t._id === incoming._id)
            if (existing) {
                Object.assign(existing, incoming)
            } else {
                state.threats.push(incoming)
            }
        },
        remoteThreatRemoved: (state, action) => {
            state.threats = state.threats.filter((t) => t._id !== action.payload)
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(createThreat.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(createThreat.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            // multiuser.md Phase 2c: this can race the entity:created socket
            // broadcast for this same threat — our own save is broadcast to
            // the room we're in too, and if that arrives before this thunk's
            // own fulfilled action, remoteThreatUpserted (threatsSlice) will
            // already have added it. Guard against pushing a duplicate.
            if (!state.threats.some((t) => t._id === action.payload._id)) {
                state.threats.push(action.payload)
            }
        })
        .addCase(createThreat.rejected, (state, action) =>{
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
        .addCase(getThreats.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(getThreats.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            state.threats = action.payload
        })
        .addCase(getThreats.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
            state.threats = []      // ✅ Clear data on error
        })
        .addCase(deleteThreat.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(deleteThreat.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            state.threats = state.threats.filter(
                (item) => item._id !== action.payload._id)
        })
        .addCase(deleteThreat.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
        .addCase(updateThreat.pending, (state) => {
            state.isLoading = true
            state.isError = false  // ✅ Reset error state
            state.message = ''     // ✅ Clear message
        })
        .addCase(updateThreat.fulfilled, (state, action) => {
            const {_id, title, description, likelihood, likelihoodValue} = action.payload
            const existingThreat = state.threats.find(threat => threat._id === _id)
            state.isLoading = false
            state.isSuccess = true
            state.isError = false  // ✅ FIXED: Reset error state
            state.message = ''     // ✅ Clear any error message
            if (existingThreat) {
                existingThreat.title = title
                existingThreat.description = description
                existingThreat.likelihood = likelihood
                existingThreat.likelihoodValue = likelihoodValue
            }
        })
        .addCase(updateThreat.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false // ✅ FIXED: Reset success state
            state.message = action.payload
        })
    }
})

export const {reset, remoteThreatUpserted, remoteThreatRemoved} = threatSlice.actions
export default threatSlice.reducer
